import { storyblokEditable } from "@storyblok/react";
import { Link } from "react-router-dom";
import { customRenderRichText } from "../utils/renderRichText";

// link resolve
const resolveStoryblokLink = (link) => {
  if (!link) return "";

  if (link.linktype === "story") {
    const slug = link.cached_url || link.story?.full_slug;
    return slug ? `/${slug}` : "";
  }

  if (link.linktype === "asset") {
    return link.url || "";
  }

  return link.url || link.cached_url || "";
};

// external and internal nav
const isInternalPath = (href) => {
  if (!href || typeof href !== "string") return false;
  const safeHref = href.trim();
  return safeHref.startsWith("/") && !safeHref.startsWith("//");
};

const SmartLink = ({ href, className, children, onClick, ...rest }) => {
  const safeHref = typeof href === "string" ? href.trim() : "";

  if (!safeHref) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </button>
    );
  }

  if (isInternalPath(safeHref)) {
    return (
      <Link
        to={safeHref}
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={safeHref}
      className={className}
      onClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
    </a>
  );
};

const SupportFeature = ({ blok }) => {
  const sectionBg = blok?.gray_background ? "bg-[#F2F2F2]" : "bg-white";

  const primaryHref = resolveStoryblokLink(blok?.primary_button_link);
  const secondaryHref = resolveStoryblokLink(blok?.secondary_button_link);

  return (
    <section
      {...storyblokEditable(blok)}
      className={`${sectionBg} w-full py-16 md:py-20`}
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-10 lg:px-16">
        {blok?.title && (
          <h2 className="mb-8 text-[40px] leading-tight font-normal text-black md:mb-10 md:text-[56px]">
            {blok.title}
          </h2>
        )}

        {blok?.body && (
          <div className="mb-10 max-w-[1400px] text-[18px] leading-[1.5] text-black md:mb-12 md:text-[24px]">
            {customRenderRichText(blok.body)}
          </div>
        )}

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-20">
          <div>
            {blok?.online_title && (
              <h3 className="mb-6 text-[24px] leading-tight font-normal text-black md:text-[32px]">
                {blok.online_title}
              </h3>
            )}

            {blok?.online_body && (
              <p className="mb-8 text-[18px] leading-[1.5] text-black md:text-[22px]">
                {blok.online_body}
              </p>
            )}

            {(blok?.primary_button_text || blok?.secondary_button) && (
              <div className="flex flex-wrap gap-4">
                {blok?.primary_button_text && (
                  <SmartLink
                    href={primaryHref}
                    className="inline-flex min-h-[56px] items-center justify-center rounded-full bg-black px-8 text-[18px] font-semibold uppercase tracking-tight text-white shadow-md transition hover:opacity-90"
                  >
                    {blok.primary_button_text}
                  </SmartLink>
                )}

                {blok?.secondary_button && (
                  <SmartLink
                    href={secondaryHref}
                    className="inline-flex min-h-[56px] items-center justify-center rounded-full bg-black px-8 text-[18px] font-semibold uppercase tracking-tight text-white shadow-md transition hover:opacity-90"
                  >
                    {blok.secondary_button}
                  </SmartLink>
                )}
              </div>
            )}
          </div>

          <div>
            {blok?.phone_title && (
              <h3 className="mb-6 text-[24px] leading-tight font-normal text-black md:text-[32px]">
                {blok.phone_title}
              </h3>
            )}

            {(blok?.phone_number || blok?.phone_hours) && (
              <div className="space-y-1 text-[18px] leading-[1.5] text-black md:text-[22px]">
                {blok?.phone_number && <p>{blok.phone_number}</p>}
                {blok?.phone_hours && <p>{blok.phone_hours}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportFeature;